import React from 'react';
import { NavLink, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderKanban,
  Wrench,
  Package,
  Images,
  FolderOpen,
  MessageSquareQuote,
  Inbox,
  Settings,
  ExternalLink,
  X,
} from 'lucide-react';
import logo from '../../logo.jpg';

export default function AdminSidebar({ isOpen, onClose, newEnquiriesCount = 0 }) {
  const navItems = [
    {
      label: 'Dashboard',
      path: '/admin',
      end: true,
      icon: <LayoutDashboard size={18} />,
    },
    {
      label: 'Projects',
      path: '/admin/projects',
      icon: <FolderKanban size={18} />,
    },
    {
      label: 'Services',
      path: '/admin/services',
      icon: <Wrench size={18} />,
    },
    {
      label: 'Packages',
      path: '/admin/packages',
      icon: <Package size={18} />,
    },
    {
      label: 'Gallery',
      path: '/admin/gallery',
      icon: <Images size={18} />,
    },
    {
      label: 'Media Library',
      path: '/admin/media',
      icon: <FolderOpen size={18} />,
    },
    {
      label: 'Testimonials',
      path: '/admin/testimonials',
      icon: <MessageSquareQuote size={18} />,
    },
    {
      label: 'Enquiries',
      path: '/admin/enquiries',
      icon: <Inbox size={18} />,
      badge: newEnquiriesCount > 0 ? newEnquiriesCount : null,
    },
    {
      label: 'Site Settings',
      path: '/admin/settings',
      icon: <Settings size={18} />,
    },
  ];

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar Panel */}
      <aside
        className={`fixed top-0 bottom-0 left-0 z-40 w-64 bg-slate-950 border-r border-slate-800/80 flex flex-col transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Brand Header */}
        <div className="h-20 px-6 border-b border-slate-800/80 flex items-center justify-between shrink-0">
          <Link to="/admin" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl bg-white/10 p-1 flex items-center justify-center border border-white/10 group-hover:border-[#9BB7A3]/50 transition">
              <img src={logo} alt="VJ Dsign Studio" className="w-8 h-8 object-contain" />
            </div>
            <div>
              <h2 className="text-sm font-extrabold text-white tracking-wider group-hover:text-[#9BB7A3] transition">
                VJ DSIGN
              </h2>
              <span className="text-[10px] font-bold text-[#9BB7A3] tracking-widest uppercase">
                ADMIN CMS
              </span>
            </div>
          </Link>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white lg:hidden"
          >
            <X size={18} />
          </button>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 py-6 px-3 overflow-y-auto space-y-1">
          <div className="px-3 pb-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
            Management
          </div>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              onClick={() => onClose && onClose()}
              className={({ isActive }) =>
                `flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 group ${
                  isActive
                    ? 'bg-[#9BB7A3] text-slate-950 shadow-md font-bold'
                    : 'text-slate-400 hover:text-slate-100 hover:bg-slate-900'
                }`
              }
            >
              <div className="flex items-center gap-3">
                <span className="transition-transform group-hover:scale-110">
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </div>
              {item.badge && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-rose-500 text-white shadow-sm">
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>

        {/* Bottom Actions: View Live Website */}
        <div className="p-4 border-t border-slate-800/80 shrink-0">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold text-slate-300 bg-slate-900 hover:bg-slate-800 hover:text-white border border-slate-800 transition group"
          >
            <span className="flex items-center gap-2">
              <ExternalLink size={15} className="text-[#9BB7A3]" />
              View Public Website
            </span>
            <span className="text-[10px] text-slate-500 group-hover:text-slate-400">↗</span>
          </a>
        </div>
      </aside>
    </>
  );
}
